import React, { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '../App';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { GraduationCap, Phone, Building, BookOpen, Calendar, HelpCircle, Upload, FileText, X, Check, Briefcase } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const ProfileModal = ({ onComplete, onSkip }) => {
  const { user } = useContext(AuthContext);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    mobile_number: '',
    college_name: '',
    degree_name: '',
    study_year: '',
    company_name: '',
    job_title: '',
    security_question: '',
    security_answer: '',
    cv_url: ''
  });
  const [customQuestion, setCustomQuestion] = useState('');
  const [cvFile, setCvFile] = useState(null);
  const [cvUploading, setCvUploading] = useState(false);
  const [existingCvName, setExistingCvName] = useState('');

  // Pre-populate form with existing user data
  useEffect(() => {
    if (user?.profile) {
      setFormData({
        mobile_number: user.profile.mobile_number || '',
        college_name: user.profile.college_name || '',
        degree_name: user.profile.degree_name || '',
        study_year: user.profile.study_year || '',
        company_name: user.profile.company_name || '',
        job_title: user.profile.job_title || '',
        security_question: '',
        security_answer: '',
        cv_url: user.profile.cv_url || ''
      });
      if (user.profile.cv_url) {
        // Extract filename from URL
        const urlParts = user.profile.cv_url.split('/');
        setExistingCvName(urlParts[urlParts.length - 1]);
      }
    }
  }, [user]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const studyYears = [
    '1st Year',
    '2nd Year',
    '3rd Year',
    '4th Year',
    'Final Year',
    'Post Graduate',
    'PhD',
    'Recent Graduate',
    'Faculty',
    'Working Professional',
    'Employer'
  ];

  const PREDEFINED_QUESTIONS = [
    "What is your mother's maiden name?",
    "What was the name of your first pet?",
    "What city were you born in?",
    "What is your favorite book?",
    "What was your first car?",
    "Write your own question"
  ];

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.mobile_number) {
      newErrors.mobile_number = 'Mobile number is required';
    } else if (!/^[6-9]\d{9}$/.test(formData.mobile_number)) {
      newErrors.mobile_number = 'Please enter a valid 10-digit mobile number';
    }
    
    if (!formData.college_name.trim()) {
      newErrors.college_name = 'College name is required';
    }
    
    if (!formData.degree_name.trim()) {
      newErrors.degree_name = 'Degree name is required';
    }
    
    if (!formData.study_year) {
      newErrors.study_year = 'Study year is required';
    }

    if (!formData.security_question && !customQuestion) {
      newErrors.security_question = 'Please select a security question';
    }
    
    if (!formData.security_answer) {
      newErrors.security_answer = 'Please provide an answer';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      const submitData = {
        ...formData,
        security_question: formData.security_question === 'Write your own question' 
          ? customQuestion 
          : formData.security_question
      };
      await onComplete(submitData);
    } catch (error) {
      console.error('Profile completion error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleCvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a PDF or Word document');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setCvFile(file);
    setCvUploading(true);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/upload-cv`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataUpload
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const data = await response.json();
      setFormData(prev => ({ ...prev, cv_url: data.url }));
      setExistingCvName(file.name);
      toast.success('CV uploaded successfully');
    } catch (error) {
      console.error('CV upload error:', error);
      toast.error('Failed to upload CV. Please try again.');
      setCvFile(null);
    } finally {
      setCvUploading(false);
    }
  };

  const removeCv = () => {
    setCvFile(null);
    setFormData(prev => ({ ...prev, cv_url: '' }));
    setExistingCvName('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Dialog open={true} onOpenChange={() => {}} modal={true}>
      <DialogContent 
        className="sm:max-w-lg max-h-[90vh] flex flex-col p-0" 
        data-testid="profile-modal"
        hideCloseButton={true}
      >
        <DialogHeader className="text-center px-6 pt-6 pb-4 flex-shrink-0">
          <DialogTitle className="text-2xl font-bold gradient-text">
            Complete Your Profile
          </DialogTitle>
          <p className="text-gray-600 mt-2 text-sm">
            This information is required to personalize your experience
          </p>
        </DialogHeader>
        
        <div className="overflow-y-auto flex-1 px-4 sm:px-6">
          <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4 pb-4">
          <div className="space-y-2">
            <Label htmlFor="mobile_number" className="text-sm font-medium text-gray-700">
              Mobile Number
            </Label>
            <div className="relative">
              <Input
                id="mobile_number"
                name="mobile_number"
                type="tel"
                value={formData.mobile_number}
                onChange={(e) => handleChange('mobile_number', e.target.value)}
                className={`pl-10 ${errors.mobile_number ? 'border-red-500' : ''}`}
                placeholder="Enter your mobile number"
                data-testid="profile-mobile-input"
              />
              <Phone className="h-4 w-4 text-gray-400 absolute left-3 top-3" />
            </div>
            {errors.mobile_number && (
              <p className="text-sm text-red-600" data-testid="mobile-error">{errors.mobile_number}</p>
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="college_name" className="text-sm font-medium text-gray-700">
              College/University Name
            </Label>
            <div className="relative">
              <Input
                id="college_name"
                name="college_name"
                type="text"
                value={formData.college_name}
                onChange={(e) => handleChange('college_name', e.target.value)}
                className={`pl-10 ${errors.college_name ? 'border-red-500' : ''}`}
                placeholder="e.g., Delhi University, IIT Bombay"
                data-testid="profile-college-input"
              />
              <Building className="h-4 w-4 text-gray-400 absolute left-3 top-3" />
            </div>
            {errors.college_name && (
              <p className="text-sm text-red-600" data-testid="college-error">{errors.college_name}</p>
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="degree_name" className="text-sm font-medium text-gray-700">
              Degree/Course Name
            </Label>
            <div className="relative">
              <Input
                id="degree_name"
                name="degree_name"
                type="text"
                value={formData.degree_name}
                onChange={(e) => handleChange('degree_name', e.target.value)}
                className={`pl-10 ${errors.degree_name ? 'border-red-500' : ''}`}
                placeholder="e.g., Computer Science, MBA, Medicine"
                data-testid="profile-degree-input"
              />
              <BookOpen className="h-4 w-4 text-gray-400 absolute left-3 top-3" />
            </div>
            {errors.degree_name && (
              <p className="text-sm text-red-600" data-testid="degree-error">{errors.degree_name}</p>
            )}
          </div>
          
          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-700">
              Current Status
            </Label>
            <div className="relative">
              <Select 
                value={formData.study_year}
                onValueChange={(value) => handleChange('study_year', value)}
                data-testid="profile-year-select"
              >
                <SelectTrigger className={`pl-10 ${errors.study_year ? 'border-red-500' : ''}`}>
                  <SelectValue placeholder="Select your current status" />
                </SelectTrigger>
                <SelectContent>
                  {studyYears.map((year) => (
                    <SelectItem key={year} value={year} data-testid={`year-option-${year}`}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Calendar className="h-4 w-4 text-gray-400 absolute left-3 top-3 pointer-events-none" />
            </div>
            {errors.study_year && (
              <p className="text-sm text-red-600" data-testid="year-error">{errors.study_year}</p>
            )}
          </div>

          {/* Company Name - Optional */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-700">
              Company Name <span className="text-gray-400 font-normal">(Optional)</span>
            </Label>
            <div className="relative">
              <Input
                type="text"
                placeholder="Enter your company name"
                value={formData.company_name}
                onChange={(e) => handleChange('company_name', e.target.value)}
                className="pl-10"
                data-testid="profile-company-input"
              />
              <Building className="h-4 w-4 text-gray-400 absolute left-3 top-3 pointer-events-none" />
            </div>
          </div>

          {/* Job Title - Optional */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-700">
              Job Title <span className="text-gray-400 font-normal">(Optional)</span>
            </Label>
            <div className="relative">
              <Input
                type="text"
                placeholder="Enter your job title"
                value={formData.job_title}
                onChange={(e) => handleChange('job_title', e.target.value)}
                className="pl-10"
                data-testid="profile-jobtitle-input"
              />
              <Briefcase className="h-4 w-4 text-gray-400 absolute left-3 top-3 pointer-events-none" />
            </div>
          </div>

          {/* CV Upload - Optional */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-700">
              Upload CV <span className="text-gray-400 font-normal">(Optional)</span>
            </Label>
            <div className="relative">
              {formData.cv_url || existingCvName ? (
                <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-green-600" />
                    <span className="text-sm text-green-700 truncate max-w-[180px]">
                      {existingCvName || 'CV Uploaded'}
                    </span>
                    <Check className="h-4 w-4 text-green-600" />
                  </div>
                  <button
                    type="button"
                    onClick={removeCv}
                    className="p-1 hover:bg-green-100 rounded-full transition-colors"
                    title="Remove CV"
                  >
                    <X className="h-4 w-4 text-green-600" />
                  </button>
                </div>
              ) : (
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className={`flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                    cvUploading 
                      ? 'border-gray-300 bg-gray-50' 
                      : 'border-gray-300 hover:border-purple-400 hover:bg-purple-50'
                  }`}
                >
                  {cvUploading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600"></div>
                      <span className="text-sm text-gray-600">Uploading...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="h-5 w-5 text-gray-400" />
                      <span className="text-sm text-gray-600">Click to upload CV (PDF, DOC, DOCX)</span>
                    </>
                  )}
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={handleCvUpload}
                className="hidden"
                data-testid="cv-upload-input"
              />
            </div>
            <p className="text-xs text-gray-500">Max file size: 5MB. Accepted formats: PDF, DOC, DOCX</p>
          </div>

          {/* Security Question */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-700">
              Security Question (for password recovery)
            </Label>
            <Select
              value={formData.security_question}
              onValueChange={(value) => handleChange('security_question', value)}
            >
              <SelectTrigger className={errors.security_question ? 'border-red-500' : ''}>
                <SelectValue placeholder="Select a security question" />
              </SelectTrigger>
              <SelectContent>
                {PREDEFINED_QUESTIONS.map((question, index) => (
                  <SelectItem key={index} value={question}>
                    {question}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.security_question && (
              <p className="text-sm text-red-600">{errors.security_question}</p>
            )}
          </div>

          {/* Custom Question Input */}
          {formData.security_question === 'Write your own question' && (
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">
                Your Custom Question
              </Label>
              <Input
                type="text"
                value={customQuestion}
                onChange={(e) => setCustomQuestion(e.target.value)}
                placeholder="e.g., What is your favorite teacher's name?"
              />
            </div>
          )}

          {/* Security Answer */}
          {formData.security_question && (
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">
                Your Answer
              </Label>
              <Input
                type="text"
                value={formData.security_answer}
                onChange={(e) => handleChange('security_answer', e.target.value)}
                placeholder="Enter your answer"
                className={errors.security_answer ? 'border-red-500' : ''}
              />
              {errors.security_answer && (
                <p className="text-sm text-red-600">{errors.security_answer}</p>
              )}
              <p className="text-xs text-gray-500">
                You'll need this to recover your password if you forget it
              </p>
            </div>
          )}
          
          <div className="pt-2">
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 font-semibold py-2.5 sm:py-3"
              data-testid="profile-submit-button"
            >
              {loading ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Saving...</span>
                </div>
              ) : (
                'Complete Profile'
              )}
            </Button>
          </div>
        </form>
        </div>
        
        <div className="text-center px-6 pb-6 flex-shrink-0 border-t pt-4">
          <p className="text-xs text-gray-500">
            This information helps us provide better recommendations and connect you with relevant peers.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProfileModal;